import Link from 'next/link'
import Autolink from 'components/Autolink'
import {
  CollectiveProposalExecutionStatus,
  CollectiveProposalQueryResult,
  CollectiveProposalStatus,
  CollectiveQueryResult,
  useThreadChangedSubscription,
  useThreadQuery
} from 'graphql/generated'
import ProposalStatusBadge from './ProposalStatusBadge'
import ProposalExecutionControls from './ProposalExecutionControls'
import ProposalVotes from './ProposalVotes'
import ProposalTallyViz from './ProposalTallyViz/ProposalTallyViz'
import ProposalTimingInfo from './ProposalTimingInfo'
import ProposalStatusBar from './ProposalStatusBar'
import EtherscanLink from '../Common/EtherscanLink'
import VoteButton from './VoteButton'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faBalanceScale,
  faExclamationTriangle
} from '@fortawesome/pro-regular-svg-icons'
import ProposalMoreActionsButton from './ProposalMoreActionsButton'
import ProposalExecutionReset from './ProposalExecutionReset'
import { ProposalBatches } from './ProposalBatches'
import { useCollectiveDemoContext } from 'context/CollectiveDemoContext'
import { faArrowAltLeft } from '@fortawesome/pro-solid-svg-icons'
import { motion } from 'framer-motion'
import CollectiveUserPopover from '../Common/CollectiveUserPopover'
import CollectivePageHeader from '../Common/CollectivePageHeader/CollectivePageHeader'
import { humanTime } from 'lib/datetime'
import ProposalTitle from './CollectiveProposalTitle/ProposalTitle'
import DetailedCommandsRenderer from './ProposalCommandsRenderer/DetailedCommandsRenderer'
import ContentWrapper from '../../ContentWrapper'
import { ForumProvider } from '../DiscussionBoard/ForumContext'
import ThreadComments from '../DiscussionBoard/ThreadComments'

export const CollectiveProposalCommandType: React.FC<{
  proposal: NonNullable<CollectiveProposalQueryResult['collectiveProposal']>
  collective: NonNullable<CollectiveQueryResult['collective']>
  demoMode?: boolean
}> = ({ proposal, collective, demoMode = false }) => {
  const { setViewingProposal, demoActiveProposal } = useCollectiveDemoContext()

  const { data } = useThreadQuery({
    variables: {
      threadId: proposal.thread?.id || '',
      collaboratorConnection: {
        first: 0
      }
    },
    skip: !proposal.thread?.id
  })

  useThreadChangedSubscription({
    variables: {
      threadId: proposal.thread?.id || ''
    },
    skip: !proposal.thread
  })

  const thread = data?.thread
  const isBotProposal = proposal.proposedBy.user.platformUser?.isBot

  return (
    <ContentWrapper>
      {!demoMode ? (
        <CollectivePageHeader collective={collective} />
      ) : (
        <div
          className="cursor-pointer flex space-x-4 my-4 ml-4 items-center text-gray-800"
          onClick={() => setViewingProposal(undefined)}
        >
          <FontAwesomeIcon icon={faArrowAltLeft} className="mr-2" /> Go Back To
          DAO
        </div>
      )}

      <div className="p-4 space-y-4">
        <div className="bg-gray-50 p-[20px] rounded-[10px]">
          <div className="flex flex-col sm:flex-row justify-between">
            <div className="flex flex-1 mr-[25px] mb-[5px]">
              <ProposalTitle icon={faBalanceScale} title={proposal.title} />
            </div>

            <div className="flex justify-end mt-2 sm:mt-0">
              <motion.div
                animate={
                  demoMode &&
                  proposal.id === demoActiveProposal?.id &&
                  !proposal.myVote
                    ? {
                        scale: [1, 1.15, 1]
                      }
                    : undefined
                }
                transition={
                  demoMode &&
                  proposal.id === demoActiveProposal?.id &&
                  !proposal.myVote
                    ? {
                        type: 'spring',
                        damping: 10,
                        stiffness: 70,
                        duration: 1.5,
                        repeat: Infinity
                      }
                    : undefined
                }
              >
                <VoteButton proposal={proposal} />
              </motion.div>

              <ProposalMoreActionsButton
                collective={collective}
                proposal={proposal}
              />
            </div>
          </div>

          <div className="pb-[5px]">
            <ProposalStatusBadge proposal={proposal} />
          </div>

          {proposal.status == CollectiveProposalStatus.EXECUTED &&
            !!proposal.execution && (
              <div className="py-[10px]">
                <span className="text-xs font-normal text-viz-green">{`Execution:`}</span>

                <p className="text-sm font-normal text-gray-700">
                  {proposal.execution.status ===
                    CollectiveProposalExecutionStatus.PENDING && (
                    <span>{`Now executing `}</span>
                  )}
                  {proposal.execution.status ===
                    CollectiveProposalExecutionStatus.SUCCEEDED && (
                    <span>{`Executed successfully `}</span>
                  )}
                  {proposal.execution.status ===
                    CollectiveProposalExecutionStatus.FAILED && (
                    <span>
                      <FontAwesomeIcon icon={faExclamationTriangle} />
                      {` Execution failed `}
                    </span>
                  )}

                  {`on block #${proposal.execution.blockNumber}, transaction hash: `}
                  <EtherscanLink
                    inline
                    shortAddress
                    network={collective.network}
                    address={proposal.execution.txHash}
                    type="tx"
                  />
                </p>
              </div>
            )}

          <div className="pt-3 flex flex-row items-center space-x-2">
            {!isBotProposal && (
              <>
                <p className="text-xs text-gray-400">
                  {proposal.isBatch ? `Batched by:` : `Proposed by:`}
                </p>

                <CollectiveUserPopover user={proposal.proposedBy.user} />
              </>
            )}

            <p className="text-xs text-gray-400">{`\u00b7 \u2004 ${humanTime(
              proposal.createdAt
            )}`}</p>
          </div>

          <ProposalExecutionReset proposal={proposal} />
          <ProposalExecutionControls proposal={proposal} />
        </div>

        {proposal.batchProposal &&
          proposal.status == CollectiveProposalStatus.EXECUTED && (
            <div>
              <p className="text-gray-700 text-sm font-medium">
                {'Executed via '}
                <Link
                  className="text-blue"
                  href={proposal.batchProposal.publicUrl}
                >
                  Batch Proposal #{proposal.batchProposal.id}
                </Link>
                {`.`}
              </p>
            </div>
          )}

        {!proposal.isBatch && (
          <>
            <ProposalStatusBar proposal={proposal} />
            <div className="border-gray-100 border-b" />

            <div className="sm:hidden">
              <ProposalTallyViz collective={collective} proposal={proposal} />

              <div className="border-b pb-2 border-gray-200" />
            </div>
          </>
        )}

        <div className="w-full flex space-x-4 pb-5">
          <div className="flex-1 overflow-hidden">
            {proposal.status === CollectiveProposalStatus.ACTIVE && (
              <div className="pb-3">
                <p className="text-gray-400 text-sm font-light">Timing:</p>
                <div>
                  <ProposalTimingInfo proposal={proposal} />
                </div>
              </div>
            )}

            {!!proposal.descriptionHtml && (
              <>
                <p className="text-gray-400 text-sm font-light">
                  Proposed details:
                </p>
                <div className="prose">
                  <Autolink as="div" newWindow lightWeight>
                    {proposal.descriptionHtml}
                  </Autolink>
                </div>
              </>
            )}

            {proposal.batchedProposals.length > 0 && (
              <>
                <p className="text-gray-400 text-sm font-light mt-10">
                  Batched Proposals:
                </p>
                <ProposalBatches proposal={proposal} />
              </>
            )}

            <p className="text-gray-400 text-sm font-light mt-10 mb-2">
              Execution commands:
            </p>
            <DetailedCommandsRenderer
              collective={collective}
              proposal={proposal}
            />
          </div>

          {!proposal.isBatch && (
            <div className="hidden sm:flex flex-1 overflow-hidden">
              <div className="hidden sm:flex border-l border-gray-200 mr-4" />

              <div className="flex-1">
                <ProposalTallyViz collective={collective} proposal={proposal} />
              </div>
            </div>
          )}
        </div>

        {thread && !proposal.isBatch && (
          <ForumProvider selectedCategoryId={thread.category.id}>
            <ThreadComments thread={thread} />
          </ForumProvider>
        )}

        {!proposal.isBatch && <ProposalVotes proposal={proposal} />}
      </div>
    </ContentWrapper>
  )
}
